import { User } from 'firebase/auth';
import { auth } from '../firebase';
import { functions, storage } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

export const emailService = {
  // Send email verification using MailerSend Cloud Function
  sendEmailVerification: async (user: User): Promise<void> => {
    try {
      const sendEmailVerificationFunction = httpsCallable(functions, 'sendEmailVerification');
      await sendEmailVerificationFunction({
        email: user.email,
        displayName: user.displayName || 'Hunter'
      });
      console.log('✅ Verification email sent successfully via MailerSend');
    } catch (error) {
      console.error('❌ Error sending verification email:', error);
      throw error;
    }
  },

  // Send password reset email using MailerSend Cloud Function
  sendPasswordResetEmail: async (email: string): Promise<void> => {
    try {
      const sendPasswordResetEmailFunction = httpsCallable(functions, 'sendPasswordResetEmail');
      await sendPasswordResetEmailFunction({ email });
      console.log('✅ Password reset email sent successfully via MailerSend');
    } catch (error) {
      console.error('❌ Error sending password reset email:', error);
      throw error;
    }
  },

  // Reload current user to get latest data (e.g., email verification status)
  reloadUser: async (): Promise<void> => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user logged in');
      }
      await user.reload();
      console.log('✅ User data reloaded successfully');
    } catch (error) {
      console.error('❌ Error reloading user data:', error);
      throw error;
    }
  },

  // Send support message using MailerSend Cloud Function
  sendSupportMessage: async (
    data: {
      name: string;
      email: string;
      subject: string;
      message: string;
    },
    attachments: File[] = [],
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    try {
      let attachmentUrls: string[] = [];

      // Upload attachments to Firebase Storage if any
      if (attachments.length > 0) {
        console.log(`📎 Uploading ${attachments.length} attachments...`);
        
        const uploadPromises = attachments.map(async (file, index) => {
          const timestamp = Date.now();
          const fileName = `support-attachments/${timestamp}-${index}-${file.name}`;
          const storageRef = ref(storage, fileName);
          
          const uploadTask = uploadBytesResumable(storageRef, file);
          
          return new Promise<string>((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                if (onProgress) {
                  const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  onProgress(Math.round(progress));
                }
              },
              (error) => {
                console.error(`❌ Error uploading ${file.name}:`, error);
                reject(error);
              },
              async () => {
                try {
                  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                  console.log(`✅ Uploaded ${file.name}: ${downloadURL}`);
                  resolve(downloadURL);
                } catch (error) {
                  reject(error);
                }
              }
            );
          });
        });

        attachmentUrls = await Promise.all(uploadPromises);
        console.log(`✅ All attachments uploaded successfully`);
      }

      // Send support message with attachment URLs
      const sendSupportMessageFunction = httpsCallable(functions, 'sendSupportMessage');
      await sendSupportMessageFunction({
        ...data,
        attachmentUrls
      });
      
      console.log('✅ Support message sent successfully via MailerSend');
    } catch (error) {
      console.error('❌ Error sending support message:', error);
      throw error;
    }
  }
};

